import * as Collapsible from '@sunwebgroup/uds-ui/collapsible'
import {
  mqMax,
  mqMin,
  space,
  SpaceBetween,
  tok,
} from '@sunwebgroup/ui-components'
import { Button, Icon, Typography, useBreakpoints } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import { useState } from 'react'
import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { getTransportSelector, routesSelector } from '@/scopes/prices/components/selectors'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(100px, 1fr));
  grid-template-rows: auto;
`

const InfoGridItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${space('primitives_space_16')};

  ${Icon} {
    margin-right: ${space('primitives_space_4')};

    @media ${mqMin('primitives_breakpoint_m_min')} {
      margin-right: ${space('primitives_space_8')};
    }
  }
`

const InfoGridRowItem = styled(InfoGridItem)`
  border-color: ${tok('semantics_dividers_color')};
  border-style: solid;
  border-width: 0 1px 2px 1px;
  justify-content: flex-start;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    justify-content: center;
  }

  &:nth-child(3n + 1) {
    border-left: none;
    justify-content: center;

    @media ${mqMin('primitives_breakpoint_m_min')} {
      justify-content: flex-start;
    }
  }

  &:nth-child(3n) {
    border-right: none;
  }

  &:nth-child(6n + 1),
  &:nth-child(6n + 2),
  &:nth-child(6n + 3) {
    background-color: ${tok('primitives_color_neutral_0')};
  }

  &:nth-child(n + 4):nth-child(-n + 6) {
    border-top: 2px solid ${tok('primitives_color_neutral_50')};
  }
`

const Content = styled(SpaceBetween)`
  max-width: 660px;
  text-align: start;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    text-align: center;
  }
`

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: ${space('primitives_space_16')};
  background-color: ${tok('primitives_color_neutral_50')};

  [data-collapsible-content] {
    ${InfoGrid}:first-of-type {
      ${InfoGridRowItem} {
        &:nth-child(n + 4):nth-child(-n + 6) {
          border-top: none;
        }
      }
    }
  }
  a {
    color: ${tok('semantics_content_secondary_color')};
  }
`

const TriggerButton = styled(Button)`
  margin-top: ${space('primitives_space_8')};
  color: ${tok('semantics_content_secondary_color')};
  &:hover {
    color: unset;
  }
  @media ${mqMax('primitives_breakpoint_s_max')} {
    display: flex;
    margin: auto;
  }
`

export function FlightTypeInfo() {
  const { inboundRoutes, outboundRoutes } = usePricesSelector(routesSelector)
  const { loading: isFetching } = usePricesSelector(getTransportSelector)
  const intl = useIntl()
  const [open, setOpen] = useState(false)

  if (isFetching || isEmpty(outboundRoutes) || isEmpty(inboundRoutes))
    return null

  return (
    <Wrapper>
      <Content direction="y" spacing={space('primitives_space_12')}>
        <Typography variant="body-m-bold-snug">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_title' })}
        </Typography>
        <Typography variant="body-m-regular-snug">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_title_text' })}
        </Typography>
        <Collapsible.Root open={open} onOpenChange={setOpen}>
          <InfoGrid>
            <TitleRow />
            <Row1 />
            <Row2 />
            <Row3 />
          </InfoGrid>
          <Collapsible.Content>
            <InfoGrid>
              <Row4 />
              <Row5 />
              <Row6 />
              <Row7 />
              <Row8 />
            </InfoGrid>
          </Collapsible.Content>
          <Collapsible.Trigger asChild>
            <TriggerButton variant="transparentPrimary" size="S">
              {intl.formatMessage({
                id: 'prices_transport_selector_flight_type_info_toggle_text',
              })}
              <Button.Icon glyph={open ? 'ChevronUp' : 'ChevronDown'} size={16} />
            </TriggerButton>
          </Collapsible.Trigger>
        </Collapsible.Root>
        <Typography
          variant="body-m-regular-snug"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_extra_text' }),
          }}
        />
      </Content>
    </Wrapper>
  )
}

function TitleRow() {
  const intl = useIntl()
  const { isTabletOrGreater } = useBreakpoints()

  return (
    <>
      <InfoGridItem />
      <InfoGridItem>
        {isTabletOrGreater && <Icon glyph="AirplaneToTop" size={16} /> }
        <Typography variant="body-s-bold-snug">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_standard_title' })}
        </Typography>
      </InfoGridItem>
      <InfoGridItem>
        {isTabletOrGreater && <Icon glyph="AirplaneWithArrowsCirclePath" /> }
        <Typography variant="body-s-bold-snug">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_flexible_title' })}
        </Typography>
      </InfoGridItem>
    </>
  )
}

function Row1() {
  const intl = useIntl()
  const { isTabletOrGreater } = useBreakpoints()

  return (
    <>
      <InfoGridRowItem>
        {isTabletOrGreater && <Icon glyph="BackpackWithCheckMark" /> }
        <Typography variant="body-xs-bold-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_handbag' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_included' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_included' })}
        </Typography>
      </InfoGridRowItem>
    </>
  )
}
function Row2() {
  const intl = useIntl()
  const { isTabletOrGreater } = useBreakpoints()

  return (
    <>
      <InfoGridRowItem>
        {isTabletOrGreater && <Icon glyph="LuggageCheck" /> }
        <Typography variant="body-xs-bold-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_checked_baggage' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography
          variant="body-xs-regular-flat"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_booked_additionally_non_flexible' }),
          }}
        />
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_booked_additionally' })}
        </Typography>
      </InfoGridRowItem>
    </>
  )
}
function Row3() {
  const intl = useIntl()
  const { isTabletOrGreater } = useBreakpoints()

  return (
    <>
      <InfoGridRowItem>
        {isTabletOrGreater && <Icon glyph="AirplaneToRight" /> }
        <Typography variant="body-xs-bold-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_change_flight' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_for_fee' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_change_flight_before_departure' })}
        </Typography>
      </InfoGridRowItem>
    </>
  )
}
function Row4() {
  const intl = useIntl()
  const { isTabletOrGreater } = useBreakpoints()

  return (
    <>
      <InfoGridRowItem>
        {isTabletOrGreater && <Icon glyph="BeachUmbrella" /> }
        <Typography variant="body-xs-bold-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_change_booking' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_for_fee' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography
          variant="body-xs-regular-flat"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_free_cancellation' }),
          }}
        />
      </InfoGridRowItem>
    </>
  )
}
function Row5() {
  const intl = useIntl()
  const { isTabletOrGreater } = useBreakpoints()

  return (
    <>
      <InfoGridRowItem>
        {isTabletOrGreater && <Icon glyph="Checklist" /> }
        <Typography variant="body-xs-bold-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_change_name' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_for_fee' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_free_one_week' })}
        </Typography>
      </InfoGridRowItem>
    </>
  )
}
function Row6() {
  const intl = useIntl()
  const { isTabletOrGreater } = useBreakpoints()

  return (
    <>
      <InfoGridRowItem>
        {isTabletOrGreater && <Icon glyph="Group" /> }
        <Typography variant="body-xs-bold-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_group_booking' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_max_persons' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_suitable' })}
        </Typography>
      </InfoGridRowItem>
    </>
  )
}
function Row7() {
  const intl = useIntl()
  const { isTabletOrGreater } = useBreakpoints()

  return (
    <>
      <InfoGridRowItem>
        {isTabletOrGreater && <Icon glyph="Wallet" /> }
        <Typography variant="body-xs-bold-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_deposit' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_flight_cost_included' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_pay_for_flight_principal' })}
        </Typography>
      </InfoGridRowItem>
    </>
  )
}
function Row8() {
  const intl = useIntl()
  const { isTabletOrGreater } = useBreakpoints()

  return (
    <>
      <InfoGridRowItem>
        {isTabletOrGreater && <Icon glyph="Wallet" /> }
        <Typography
          variant="body-xs-regular-flat"
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_exchange-guarantee' }),
          }}
        />
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_flight_rebooking' })}
        </Typography>
      </InfoGridRowItem>
      <InfoGridRowItem>
        <Typography variant="body-xs-regular-flat">
          {intl.formatMessage({ id: 'prices_transport_selector_flight_type_info_six-week-departure' })}
        </Typography>
      </InfoGridRowItem>
    </>
  )
}
